import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faSearch, faPlus } from '@fortawesome/free-solid-svg-icons';
import groupService from '../../api/groupService';
import calendarApi from '../../services/calendarApi';
import EventCard from './EventCard'; // Import the EventCard component
import CreateClassModal from './CreateClassModal'; // Import the CreateClassModal component
import { AdminPageLayout, AdminPageFilters } from '../common';
import '../../styles/group/ClassesList.css';
import '../../styles/FinancialsPage.css'; // For chip filter styles

// Utility function to check if a class is past (3 hours after start time)
// Recurring classes never become "past"
const isClassPast = (classDate, isRecurring = false) => {
  // If it's a recurring class, it never becomes past
  if (isRecurring) {
    return false;
  }
  
  const now = new Date();
  const classStart = new Date(classDate);
  const threeHoursAfterClass = new Date(classStart.getTime() + (3 * 60 * 60 * 1000)); // Add 3 hours
  return now > threeHoursAfterClass;
};

// Utility function to check if a class is upcoming (within next 30 days)
const isClassUpcoming = (classDate) => {
  const now = new Date();
  const classStart = new Date(classDate);
  const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000)); // Add 30 days
  
  return classStart >= now && classStart <= thirtyDaysFromNow;
};

const ClassesList = ({ groupId, searchQuery, isAdmin, onDataLoad }) => {
  const [classes, setClasses] = useState([]);
  const [classCategories, setClassCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasApiError, setHasApiError] = useState(false); // Track if we had API errors
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentClass, setCurrentClass] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all'); // Active category filter
  const [sortBy, setSortBy] = useState('newest');
  const [activeCalendar, setActiveCalendar] = useState(null);

  // Fetch default calendar for this group
  useEffect(() => {
    const fetchDefaultCalendar = async () => {
      if (!groupId) return;
      
      try {
        const response = await calendarApi.getCalendars(groupId);
        const calendarsData = response.data || [];
        const defaultCalendar = calendarsData.find(c => c.is_default) || calendarsData[0];
        setActiveCalendar(defaultCalendar?.id || null);
      } catch (err) {
        console.error('Error fetching calendars:', err);
      }
    };

    fetchDefaultCalendar();
  }, [groupId]);

  // Fetch classes and categories for this group
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        setHasApiError(false); // Reset API error flag
        
        // Fetch both classes and categories in parallel
        const [classesResponse, categoriesResponse] = await Promise.all([
          groupService.getGroupClasses(groupId),
          groupService.getClassCategories(groupId)
        ]);
        
        // Ensure classes is always an array
        const fetchedClasses = Array.isArray(classesResponse) ? classesResponse : 
                              (classesResponse && Array.isArray(classesResponse.data)) ? classesResponse.data : [];
        
        // Transform class data to match EventCard expectations
        const transformedClasses = fetchedClasses.map(classItem => ({
          ...classItem,
          // Ensure required fields for EventCard exist
          id: classItem.id || Math.random().toString(),
          title: classItem.title || 'Unnamed Class',
          description: classItem.description || 'No description available',
          date: classItem.date || new Date().toISOString(),
          cover_image_url: classItem.image_url || classItem.cover_image_url,
          teacher: classItem.instructor || classItem.teacher || 'No instructor specified'
        }));
        
        console.log('Transformed class data for EventCard:', transformedClasses);
        setClasses(transformedClasses);
        setClassCategories(categoriesResponse.data || []);
        
        // Call onDataLoad callback if provided
        if (onDataLoad) {
          onDataLoad(transformedClasses);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching classes:', error);
        setHasApiError(true); // Set API error flag
        setError('Please refresh the page to reload classes');
        setLoading(false);
      }
    };

    if (groupId) {
      fetchClasses();
    }
  }, [groupId]); // Remove onDataLoad from dependency array to prevent infinite loop

  // Handle edit class
  const handleEditClass = (classItem) => {
    setCurrentClass(classItem);
    setShowEditModal(true);
  };

  // Handle class update
  const handleClassUpdated = (updatedClass) => {
    setClasses(classes.map(classItem => 
      classItem.id === updatedClass.id ? {
        ...updatedClass,
        // Ensure required fields for EventCard exist and are preserved
        id: updatedClass.id || classItem.id,
        title: updatedClass.title || 'Unnamed Class',
        description: updatedClass.description || 'No description available',
        date: updatedClass.date || classItem.date,
        // Don't fallback to old image if it was explicitly removed (null)
        cover_image_url: updatedClass.hasOwnProperty('cover_image_url') ? updatedClass.cover_image_url : (updatedClass.image_url || classItem.cover_image_url),
        teacher: updatedClass.teacher || updatedClass.instructor || classItem.teacher || 'No instructor specified'
      } : classItem
    ));
    
    // If the updated class has a category that's not in our list, add it
    if (updatedClass.category && !classCategories.includes(updatedClass.category)) {
      setClassCategories([...classCategories, updatedClass.category]);
    }
    
    setShowEditModal(false);
    setCurrentClass(null);
  };

  // Handle delete class
  const handleDeleteClass = async (classId) => {
    try {
      console.log(`Attempting to delete class with ID: ${classId}`);
      
      // Explicitly validate the classId before sending the request
      if (!classId) {
        console.error('Invalid class ID for deletion:', classId);
        alert('Cannot delete class: Invalid class ID');
        return;
      }
      
      const response = await groupService.deleteClass(groupId, classId);
      console.log('Delete class response:', response);
      
      // Only update the UI if deletion was successful
      if (response && response.success) {
        setClasses(classes.filter(classItem => classItem.id !== classId));
        console.log(`Class ${classId} deleted successfully and removed from UI`);
      } else {
        console.error('Server returned unsuccessful response:', response);
        alert('Failed to delete class. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting class:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert('Failed to delete class. Please try again.');
    }
  };

  // Handle create new class
  const handleCreateNew = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  // Prepare filter components
  const renderFilters = useCallback(() => {
    if (!isAdmin) return null;
    
    // Get unique categories with counts
    const uniqueCategories = classCategories.map(category => ({
      value: category,
      label: category,
      count: classes.filter(c => c.category === category).length
    }));
    
    return (
      <>
        <div className="chip-filters-container">
          <div className="chip-filters">
            <button
              className={`chip-filter ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => setActiveCategory('all')}
            >
              All
            </button>
            {uniqueCategories.map(category => (
              <button
                key={category.value}
                className={`chip-filter ${activeCategory === category.value ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.value)}
              >
                <span>{category.label}</span>
              </button>
            ))}
          </div>
          
          {/* Right side controls - Sort and New button */}
          <div className="right-controls">
            <AdminPageFilters.SortDropdown
              value={sortBy}
              onChange={setSortBy}
              options={[
                { value: 'newest', label: 'Newest' },
                { value: 'oldest', label: 'Oldest' },
                { value: 'name', label: 'Name' },
                { value: 'teacher', label: 'Teacher' }
              ]}
            />
            
            <button 
              className="btn btn-primary"
              data-tour="new-class-button"
              onClick={handleCreateNew}
              style={{
                backgroundColor: '#5669FF',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
              New
            </button>
          </div>
        </div>
      </>
    );
  }, [isAdmin, activeCategory, classCategories, classes, sortBy, handleCreateNew]);

  // Handle class created
  const handleClassCreated = (newClass) => {
    setClasses([newClass, ...classes]);
    
    // If the new class has a category that's not in our list, add it
    if (newClass.category && !classCategories.includes(newClass.category)) {
      setClassCategories([...classCategories, newClass.category]);
    }
    
    setShowCreateModal(false);
  };

  // Filter classes based on active category
  let filteredClasses = Array.isArray(classes) ? classes : [];

  // Apply category filter
  if (activeCategory && activeCategory !== 'all') {
    filteredClasses = filteredClasses.filter(classItem => classItem.category === activeCategory);
  }

  // Apply sorting
  const sortedClasses = [...filteredClasses].sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return new Date(a.date) - new Date(b.date);
      case 'name':
        return a.title.localeCompare(b.title);
      case 'teacher':
        return (a.teacher || '').localeCompare(b.teacher || '');
      case 'newest':
      default:
        return new Date(b.date) - new Date(a.date);
    }
  });

  // Handle page refresh
  const handleRefreshPage = () => {
    window.location.reload();
  };

  // Helper function to render empty state content
  const renderEmptyState = () => {
    // Show error state if we detected API errors
    if (hasApiError || error) {
      if (isAdmin) {
        return (
          <div className="empty-state">
            <div className="empty-state-icon">
              <FontAwesomeIcon icon={faBookOpen} size="4x" />
            </div>
            <h2>Something went wrong...</h2>
            <p>Unable to load classes</p>
            <button 
              className="try-again-button"
              onClick={handleRefreshPage}
            >
              Try Again
            </button>
          </div>
        );
      } else {
        return (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Unable to load classes. Please try again later.</p>
          </div>
        );
      }
    }
    
    // Show different empty state for admin vs member
    if (isAdmin) {
      return (
        <div className="classes-empty-state">
          <span className="empty-state-emoji">📚</span>
          <h3>No Classes Yet</h3>
          <p>Create your first class in the Yesod app to share with your community.</p>
          <p>Only approved members who have access to your community will see your classes.</p>
          <p className="empty-state-action-hint">Tap the <strong>"New"</strong> button above to get started.</p>
        </div>
      );
    } else {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>No classes yet.</p>
        </div>
      );
    }
  };

  const headerStats = (
    <div className="stats-grid">
      <div className="stat-card">
        <div className="stat-icon bg-green-100">
          <FontAwesomeIcon icon={faBookOpen} className="text-green-600" />
        </div>
        <div className="stat-content">
          <div className="stat-label">Total Classes</div>
          <div className="stat-number">{Array.isArray(classes) ? classes.length : 0}</div>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon bg-blue-100">
          <FontAwesomeIcon icon={faBookOpen} className="text-blue-600" />
        </div>
        <div className="stat-content">
          <div className="stat-label">With Category</div>
          <div className="stat-number">{Array.isArray(classes) ? classes.filter(classItem => classItem.category).length : 0}</div>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon bg-gray-100">
          <FontAwesomeIcon icon={faBookOpen} className="text-gray-600" />
        </div>
        <div className="stat-content">
          <div className="stat-label">Categories</div>
          <div className="stat-number">{classCategories.length}</div>
        </div>
      </div>
    </div>
  );

  const headerActions = null;

  // Conditional wrapper component for member vs admin view
  const LayoutWrapper = ({ children }) => {
    if (isAdmin) {
      return (
        <AdminPageLayout
          className="community-feed-seamless"
          title="Classes"
          icon={<FontAwesomeIcon icon={faBookOpen} />}
          subtitle="Add and manage your community's classes here. Whatever you add will be visible to members in their app."
          headerActions={headerActions}
          stats={headerStats}
          showFilters={isAdmin}
          filters={renderFilters()}
          loading={loading}
          error={error}
          emptyState={sortedClasses.length === 0 ? renderEmptyState() : null}
        >
          {children}
        </AdminPageLayout>
      );
    } else {
      // Member view - just show content without dashboard headers
      return (
        <div className="member-classes-container">
          {sortedClasses.length === 0 ? renderEmptyState() : children}
        </div>
      );
    }
  };

  return (
    <LayoutWrapper>
      {Array.isArray(sortedClasses) && sortedClasses.length > 0 ? (
        <div className="classes-list">
          {sortedClasses.map(classItem => (
            <EventCard 
              key={classItem.id}
              event={classItem} 
              groupId={groupId}
              isAdmin={isAdmin}
              isClass={true} // Important: This flag tells EventCard to render as a class card
              onEditEvent={handleEditClass}
              onDeleteEvent={handleDeleteClass}
            />
          ))}
        </div>
      ) : (
        renderEmptyState()
      )}
      
      {/* Create Class Modal */}
      {showCreateModal && (
        <CreateClassModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          groupId={groupId}
          onClassCreated={handleClassCreated}
          activeCalendar={activeCalendar}
          hideCalendarSelector={true}
        />
      )}
      
      {/* Edit Class Modal */}
      {showEditModal && (
        <CreateClassModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setCurrentClass(null);
          }}
          groupId={groupId}
          onClassUpdated={handleClassUpdated}
          classItem={currentClass}
          isEditMode={true}
          activeCalendar={activeCalendar}
          hideCalendarSelector={true}
        />
      )}
    </LayoutWrapper>
  );
};

export default ClassesList;